import { useEffect, useRef, useState } from "react";
import { useWavesurfer } from '@wavesurfer/react'

const PLAYBACK_LOAD_TIMEOUT_MS = 15000;

export function useWavesurferPlayback({
  playbackAudioUrl,
  muted,
  playBackState = "pause",
  onPlaybackStateChange,
}) {
  const containerRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const mutedRef = useRef(muted)
  const onPlaybackStateChangeRef = useRef(onPlaybackStateChange)
  mutedRef.current = muted
  onPlaybackStateChangeRef.current = onPlaybackStateChange
  
  const { wavesurfer } = useWavesurfer({
    container: containerRef,
    height: 60,
    waveColor: '#535A63',
    progressColor: '#EE772F',
    barHeight: 1,
    barWidth: 2,
    barRadius: 4,
    barGap: 4,
    url: playbackAudioUrl,
    dragToSeek: true,
    backend: "WebAudio"
  })

  useEffect(() => {
    return () => {
      if (wavesurfer) {
        try {
          if (wavesurfer.isPlaying()) {
            wavesurfer.stop();
          }
          wavesurfer.destroy();
        } catch (err) {
          console.error("Error destroying wavesurfer in Playback:", err);
        }
      }
    };
  }, [wavesurfer]);

  useEffect(() => {
    if (!wavesurfer || !playbackAudioUrl) return;

    setIsLoading(true);
    setLoadError(false);

    const loadTimeoutId = setTimeout(() => {
      console.warn("Playback load timeout");
      setIsLoading(false);
      setLoadError(true);
    }, PLAYBACK_LOAD_TIMEOUT_MS);

    const markReady = () => {
      clearTimeout(loadTimeoutId);
      setIsLoading(false);
      setLoadError(false);
    };

    const handleReady = () => markReady();

    const handleError = (err) => {
      console.error("Playback error:", err);
      clearTimeout(loadTimeoutId);
      setIsLoading(false);
      setLoadError(true);
    };

    const handlePlay = () => {
      if (mutedRef.current) {
        wavesurfer.setVolume(0);
        setTimeout(() => {
          wavesurfer.setVolume(1);
        }, 2000);
      }
    };

    const handleFinish = () => {
      onPlaybackStateChangeRef.current('pause');
    };

    wavesurfer.on("ready", handleReady);
    wavesurfer.on("error", handleError);
    wavesurfer.on("play", handlePlay);
    wavesurfer.on("finish", handleFinish);

    try {
      const duration = wavesurfer.getDuration();
      if (Number.isFinite(duration) && duration > 0) {
        markReady();
      }
    } catch {
    }

    return () => {
      clearTimeout(loadTimeoutId);
      wavesurfer.un("ready", handleReady);
      wavesurfer.un("error", handleError);
      wavesurfer.un("play", handlePlay);
      wavesurfer.un("finish", handleFinish);
    };
  }, [playbackAudioUrl, wavesurfer]);

  useEffect(() => {
    if (!wavesurfer) return;

    if (playBackState === "pause" && wavesurfer.isPlaying()) {
      wavesurfer.pause();
    } else if (playBackState === "resume" && !wavesurfer.isPlaying()) {
      setIsLoading(false);
      setLoadError(false);
      wavesurfer.play().catch((err) => console.warn("Playback play failed:", err));
    }
  }, [playBackState, wavesurfer]);

  return { containerRef, isLoading, loadError };
}
